import { changeUsersListFileName } from 'actions/import';
import {
  addUsers,
  clearState,
  selectUsers,
  setImportErrors,
  setIsImporting,
} from 'actions/userPermissionsPage';
import { CLOSE_ICON } from 'constants/envelopeIcons';
import useAllTeams from 'hooks/teams/useAllTeams';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getImportUsersListFileName } from 'selectors/import';
import { useSelector } from 'store';
import { loadAndParseXLSXToJSON, parseUsersFromXLSXToJSON } from 'utils/files';

const DragAndDropFile: React.FC = () => {
  const dispatch = useDispatch();

  const { data: teams } = useAllTeams();
  const fileName = useSelector(getImportUsersListFileName);

  const dropZoneRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const handleWindowDrop = (e: React.DragEvent<HTMLDivElement>): void => {
      if ([...e.dataTransfer.items].some((item) => item.kind === 'file')) {
        e.preventDefault();
      }
    };

    // @ts-ignore
    window.addEventListener('drop', handleWindowDrop);
    return () => {
      // @ts-ignore
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, []);

  const handleDragEnter = (): void => setIsDragging(true);
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    // @ts-ignore
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  };

  const parseUsersFile = (file: File): void => {
    loadAndParseXLSXToJSON(file, (sheets) => {
      const { users, errors } = parseUsersFromXLSXToJSON(sheets, teams?.records || []);

      dispatch(addUsers(users));
      dispatch(setIsImporting(true));
      dispatch(setImportErrors(errors));
      dispatch(selectUsers(users));
    });
  };

  const loadFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    dispatch(changeUsersListFileName(file.name));
    parseUsersFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    if (fileName) return;
    e.stopPropagation();
    e.preventDefault();

    const file = e.dataTransfer.items[0]?.getAsFile();

    if (!file) return;

    setIsDragging(false);
    dispatch(changeUsersListFileName(file.name));
    parseUsersFile(file);
  };

  const unselectFile = (): void => {
    dispatch(changeUsersListFileName(''));
    dispatch(clearState());
  };

  return (
    <div className="flex flex-col gap-4">
      <span>User list for the list of reviewers and monitored cohort</span>
      <div
        ref={dropZoneRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        className={`w-full h-full min-h-43 flex items-center justify-center border rounded  border-litlingo-gray-2 ${
          isDragging ? 'bg-litlingo-success-light' : 'bg-white'
        }`}
      >
        <div className="flex flex-col gap-3 justify-center items-center">
          <svg
            width="42"
            height="48"
            viewBox="0 0 42 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M33.4128 16.848C33.5328 17.072 33.6 17.328 33.6 17.6V46.4C33.6 47.2832 32.8832 48 32 48H1.6C0.7168 48 0 47.2832 0 46.4V9.6C0 8.7168 0.7168 8 1.6 8H24C24.272 8 24.528 8.0672 24.752 8.1872L24.7568 8.1888C24.88 8.256 24.9968 8.3408 25.104 8.4416L25.1312 8.4688L33.1312 16.4688L33.1584 16.496C33.2592 16.6032 33.344 16.72 33.4096 16.8432L33.4128 16.848ZM22.4 11.2H3.2V44.8H30.4V19.2H24C23.1168 19.2 22.4 18.4832 22.4 17.6V11.2ZM41.4128 8.848C41.5328 9.072 41.6 9.328 41.6 9.6V40C41.6 40.8832 40.8832 41.6 40 41.6H36.8C35.9168 41.6 35.2 40.8832 35.2 40C35.2 39.1168 35.9168 38.4 36.8 38.4H38.4V11.2H32C31.1168 11.2 30.4 10.4832 30.4 9.6V3.2H9.6V4.8C9.6 5.6832 8.8832 6.4 8 6.4C7.1168 6.4 6.4 5.6832 6.4 4.8V1.6C6.4 0.7168 7.1168 0 8 0H32C32.272 0 32.528 0.0672 32.752 0.1872L32.7568 0.1888C32.88 0.256 32.9968 0.3408 33.104 0.4416L33.1312 0.4688L41.1312 8.4688L41.1584 8.496C41.2592 8.6032 41.344 8.72 41.4096 8.8432L41.4128 8.848ZM9.6 38.4H17.6C18.4832 38.4 19.2 37.6832 19.2 36.8C19.2 35.9168 18.4832 35.2 17.6 35.2H9.6C8.7168 35.2 8 35.9168 8 36.8C8 37.6832 8.7168 38.4 9.6 38.4ZM9.6 32H24C24.8832 32 25.6 31.2832 25.6 30.4C25.6 29.5168 24.8832 28.8 24 28.8H9.6C8.7168 28.8 8 29.5168 8 30.4C8 31.2832 8.7168 32 9.6 32ZM9.6 25.6H24C24.8832 25.6 25.6 24.8832 25.6 24C25.6 23.1168 24.8832 22.4 24 22.4H9.6C8.7168 22.4 8 23.1168 8 24C8 24.8832 8.7168 25.6 9.6 25.6ZM25.6 16H28.1376L25.6 13.4624V16ZM33.6 8H36.1376L33.6 5.4624V8Z"
              fill="black"
            />
          </svg>

          {!fileName ? (
            <p className="text-heading-3">
              Drop your file here, or{' '}
              <label
                htmlFor="file-input"
                className="text-litlingo-primary underline cursor-pointer"
              >
                Browse
                <input id="file-input" type="file" className="hidden" onChange={loadFile} />
              </label>
            </p>
          ) : (
            <div className="flex flex-row gap-4">
              <p className="text-heading-3">{fileName}</p>
              <button type="button" className="h-5 w-5" onClick={unselectFile}>
                {CLOSE_ICON}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DragAndDropFile;
